import {
  createLlmGatewayConfiguration,
  fetchLlmGatewayConfiguration,
  requestLlmGatewayAccess,
  selectLlmGatewayConfiguration,
  selectLlmGatewaySelfServiceEnrollmentEnabled,
} from "@app/deploy";
import type { LoadingState } from "@app/fx";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useCompositeLoader,
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { llmGatewayUrl } from "@app/routes";
import { tunaEvent } from "@app/tuna";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AppSidebarLayout } from "../layouts";
import { AiTermsConsent } from "../shared/ai-terms";
import { Banner, BannerMessages } from "../shared/banner";
import { Button } from "../shared/button";
import { Group } from "../shared/group";
import { IconCheckCircle, IconX } from "../shared/icons";
import { LoadingSpinner } from "../shared/loading";
import { ModalWrapper } from "../shared/modal-portal";
import { TitleBar } from "../shared/resource-list-view";
import { ProvidersBadge } from "../shared/supported-models";

const ACCENT_COLOR = "#283a99";

const FEATURE_GROUPS: {
  group: string;
  items: { label: string; text: string }[];
}[] = [
  {
    group: "LLM Access & Control",
    items: [
      {
        label: "Multi-Provider Support",
        text: "Use Anthropic, OpenAI, and Amazon Bedrock from a single API",
      },
      {
        label: "Model Access & Key Management",
        text: "Grant, revoke, and scope model access per key. Set team-level model access controls.",
      },
      {
        label: "Cost Visibility & Control",
        text: "View usage costs by key and by request, and set cost limits across your team",
      },
    ],
  },
  {
    group: "Security and Compliance",
    items: [
      {
        label: "Encryption",
        text: "All data encrypted in transit and at rest",
      },
      {
        label: "Audit Logging",
        text: "Every LLM request and response is logged and stored by Aptible",
      },
      {
        label: "BAA Coverage",
        text: "Supported models and capabilities covered under the Aptible BAA",
      },
      {
        label: "PHI Handling",
        text: "Prompts and responses are never used for model training",
      },
    ],
  },
];

const TITLE_DESCRIPTION =
  "LLM keys provide secure access to the Aptible LLM Gateway for your applications.";

const ACCESS_REQUEST_LEAD =
  "LLM Gateway access is approved by our team. Request access and we'll email you when your trial is ready.";

const TrialCallToAction = ({
  lead,
  loader,
  onClick,
  buttonLabel,
  consentAction,
}: {
  lead: string;
  loader: LoadingState;
  onClick: () => void;
  buttonLabel: string;
  consentAction: string;
}) => (
  <>
    <p className="pt-5 text-gray-700">{lead}</p>
    <BannerMessages className="mt-2" {...loader} />
    <div className="pt-6">
      <Button
        onClick={onClick}
        isLoading={loader.isLoading}
        variant="white"
        layout="block"
      >
        {buttonLabel}
      </Button>
    </div>
    <div className="pt-2">
      <AiTermsConsent action={consentAction} />
    </div>
  </>
);

const AccessRequestCallToAction = ({
  loader,
  onClick,
}: {
  loader: LoadingState;
  onClick: () => void;
}) => {
  if (loader.isSuccess) {
    return (
      <div className="pt-5">
        <Banner variant="success">
          Request received. Our team reviews LLM Gateway access requests and
          will email you when your trial is approved. Your Aptible account is
          ready to use in the meantime.
        </Banner>
      </div>
    );
  }

  return (
    <TrialCallToAction
      lead={ACCESS_REQUEST_LEAD}
      loader={loader}
      onClick={onClick}
      buttonLabel="Request access"
      consentAction="requesting access"
    />
  );
};

export const AiGatewayEnrollPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchLlmGatewayConfiguration,
  ]);
  const orgId = useSelector(selectOrganizationSelectedId);
  const config = useSelector(selectLlmGatewayConfiguration);
  const isEnrolled = !!config?.id;
  const enrollmentEnabled = useSelector(
    selectLlmGatewaySelfServiceEnrollmentEnabled,
  );

  const enrollLoader = useLoader(createLlmGatewayConfiguration);

  useLoaderSuccess(enrollLoader, () => {
    navigate(llmGatewayUrl());
  });

  const handleEnroll = () => {
    tunaEvent("llm-gateway-enroll-clicked", JSON.stringify({ orgId }));
    dispatch(createLlmGatewayConfiguration({ orgId }));
  };

  const accessRequestLoader = useLoader(requestLlmGatewayAccess);

  useLoaderSuccess(accessRequestLoader, () => {
    dispatch(fetchLlmGatewayConfiguration({ orgId }));
  });

  const handleRequestAccess = () => {
    tunaEvent("llm-gateway-access-request-clicked", JSON.stringify({ orgId }));
    dispatch(requestLlmGatewayAccess({ orgId }));
  };

  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsLightboxOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isLightboxOpen]);

  if (isLoading || isInitialLoading) {
    return (
      <AppSidebarLayout>
        <Group>
          <Group size="sm">
            <TitleBar description={TITLE_DESCRIPTION}>LLM Gateway</TitleBar>
          </Group>
          <div className="flex justify-center py-12">
            <LoadingSpinner show />
          </div>
        </Group>
      </AppSidebarLayout>
    );
  }

  if (isEnrolled) {
    return <Navigate to={llmGatewayUrl()} replace />;
  }

  return (
    <AppSidebarLayout>
      <Group>
        <Group size="sm">
          <TitleBar description={TITLE_DESCRIPTION}>LLM Gateway</TitleBar>
        </Group>

        <div className="rounded-lg">
          <div className="bg-white shadow border border-black-100 rounded-lg overflow-hidden flex flex-col xl:flex-row items-stretch">
            <div
              className="order-2 xl:order-none flex flex-col items-center justify-center px-6 pt-10 pb-6 min-h-[320px] xl:min-h-[480px] xl:flex-[0.42] rounded-b-lg xl:rounded-b-none xl:rounded-l-lg"
              style={{ backgroundColor: ACCENT_COLOR }}
            >
              <button
                type="button"
                onClick={() => setIsLightboxOpen(true)}
                className="block w-full max-w-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#283a99] rounded-lg"
                aria-label="View LLM Gateway screenshot larger"
                title="Click to enlarge"
              >
                <img
                  src="/assets/images/llm-keys-landing.png"
                  alt="LLM Gateway"
                  className="max-w-full max-h-[520px] w-full h-auto object-contain cursor-pointer"
                />
                <span className="sr-only">Click to view larger</span>
              </button>
            </div>

            <div className="order-1 xl:order-none flex flex-col p-6 xl:p-8 xl:flex-[0.58] xl:min-w-0 min-h-0 rounded-t-lg xl:rounded-t-none xl:rounded-r-lg">
              <div
                className="text-xs uppercase tracking-wide font-medium"
                style={{ color: ACCENT_COLOR }}
              >
                NEW FEATURE
              </div>
              <h1 className="text-2xl font-bold text-black pt-2">
                Get started with the LLM Gateway
              </h1>
              <p className="pt-3 text-gray-700">
                Aptible LLM Gateway is a single API to access Anthropic, OpenAI,
                and Amazon Bedrock with security and compliance guardrails built
                in. Manage keys, control model access, and use LLMs without
                having to think about the security layer.
              </p>

              <div className="space-y-4 pt-5">
                {FEATURE_GROUPS.map(({ group, items }) => (
                  <div key={group}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      {group}
                    </p>
                    <ul className="space-y-2">
                      {items.map(({ label, text }) => (
                        <li key={label} className="flex gap-3 items-start">
                          <IconCheckCircle
                            color={ACCENT_COLOR}
                            variant="sm"
                            className="flex-shrink-0 mt-0.5"
                          />
                          <span>
                            <strong>{label}:</strong> {text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <ProvidersBadge />
              </div>

              {enrollmentEnabled ? (
                <TrialCallToAction
                  lead="Try it free with $10 in credits. No commitment required."
                  loader={enrollLoader}
                  onClick={handleEnroll}
                  buttonLabel="Try for free"
                  consentAction="enrolling"
                />
              ) : (
                <AccessRequestCallToAction
                  loader={accessRequestLoader}
                  onClick={handleRequestAccess}
                />
              )}
            </div>
          </div>

          {isLightboxOpen && (
            <ModalWrapper>
              <div
                className="fixed inset-0 z-10 flex items-center justify-center p-4 bg-black/70"
                role="dialog"
                aria-modal="true"
                aria-label="LLM Gateway screenshot (enlarged)"
                onClick={() => setIsLightboxOpen(false)}
                onKeyDown={(e) =>
                  e.key === "Escape" && setIsLightboxOpen(false)
                }
              >
                <button
                  type="button"
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white text-gray-700 z-20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsLightboxOpen(false);
                  }}
                  aria-label="Close"
                >
                  <IconX className="w-6 h-6" />
                </button>
                <img
                  src="/assets/images/llm-keys-landing.png"
                  alt="LLM Gateway screenshot"
                  className="max-w-full max-h-[calc(100vh-4rem)] w-auto h-auto object-contain"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </ModalWrapper>
          )}
        </div>
      </Group>
    </AppSidebarLayout>
  );
};
